import { Card, CardContent } from "@/components/ui/card";
import { Heart, Activity, TrendingUp } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

// Types for the health snapshot data
export interface HealthSnapshotData {
  bloodPressure: string;
  heartRate: string;
  temperature: string;
  weight: string;
  healthScore: number;
  lastUpdated: string;
}

interface HealthSnapshotProps {
  data: HealthSnapshotData;
  className?: string;
  variant?: "sidebar" | "header" | "card";
  collapsed?: boolean;
}

export function HealthSnapshot({ 
  data, 
  className, 
  variant = "card",
  collapsed = false 
}: HealthSnapshotProps) {
  // Helper function to get health score color
  const getHealthScoreColor = (score: number) => {
    if (score >= 80) return "text-green-600";
    if (score >= 60) return "text-yellow-600";
    return "text-red-600";
  };

  // Collapsed view just shows icons with tooltips
  if (collapsed) {
    return (
      <TooltipProvider>
        <div className={cn(
          "flex items-center gap-2 p-2",
          variant === "sidebar" && "flex-col",
          variant === "header" && "flex-row justify-end",
          className
        )}>
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="flex items-center gap-1 text-sm">
                <Heart className="h-4 w-4 text-red-500" />
                <span className="font-medium">{data.heartRate}</span>
              </div>
            </TooltipTrigger>
            <TooltipContent>
              <p>Heart Rate: {data.heartRate}</p>
            </TooltipContent>
          </Tooltip>
          
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="flex items-center gap-1 text-sm">
                <TrendingUp className="h-4 w-4 text-blue-500" />
                <span className="font-medium">{data.bloodPressure}</span>
              </div>
            </TooltipTrigger>
            <TooltipContent>
              <p>Blood Pressure: {data.bloodPressure}</p>
            </TooltipContent>
          </Tooltip>
          
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="flex items-center gap-1 text-sm">
                <Activity className="h-4 w-4 text-purple-500" />
                <span className={cn("font-medium", getHealthScoreColor(data.healthScore))}>
                  {data.healthScore}
                </span>
              </div>
            </TooltipTrigger>
            <TooltipContent>
              <p>Health Score: {data.healthScore}</p>
            </TooltipContent>
          </Tooltip>
        </div>
      </TooltipProvider>
    );
  }

  // Full view based on variant
  if (variant === "sidebar") {
    return (
      <div className={cn("space-y-3 p-3", className)}>
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium">Health Snapshot</span>
          <span className="text-xs text-muted-foreground">
            {data.lastUpdated}
          </span>
        </div>
        
        <div className="space-y-2">
          <div className="flex justify-between items-center">
            <span className="text-xs text-muted-foreground flex items-center gap-1">
              <Heart className="h-3 w-3 text-red-500" /> Heart Rate
            </span>
            <span className="text-sm font-medium">{data.heartRate}</span>
          </div>
          
          <div className="flex justify-between items-center">
            <span className="text-xs text-muted-foreground flex items-center gap-1">
              <TrendingUp className="h-3 w-3 text-blue-500" /> BP
            </span>
            <span className="text-sm font-medium">{data.bloodPressure}</span>
          </div>
          
          <div className="flex justify-between items-center">
            <span className="text-xs text-muted-foreground flex items-center gap-1">
              <Activity className="h-3 w-3 text-purple-500" /> Health Score
            </span>
            <span className={cn("text-sm font-medium", getHealthScoreColor(data.healthScore))}>
              {data.healthScore}
            </span>
          </div>
        </div>
      </div>
    );
  }

  if (variant === "header") {
    return (
      <div className={cn("flex items-center gap-4", className)}>
        <div className="flex items-center gap-1">
          <Heart className="h-4 w-4 text-red-500" />
          <span className="text-sm font-medium">{data.heartRate}</span>
        </div>
        
        <div className="flex items-center gap-1">
          <TrendingUp className="h-4 w-4 text-blue-500" />
          <span className="text-sm font-medium">{data.bloodPressure}</span>
        </div>
        
        <div className="flex items-center gap-1">
          <Activity className="h-4 w-4 text-purple-500" />
          <span className={cn("text-sm font-medium", getHealthScoreColor(data.healthScore))}>
            {data.healthScore}
          </span>
        </div>
        
        <span className="text-xs text-muted-foreground">
          Updated: {data.lastUpdated}
        </span>
      </div>
    );
  }

  // Default card view
  return (
    <Card className={className}>
      <CardContent className="pt-6 space-y-3">
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium">Health Snapshot</span>
          <span className="text-xs text-muted-foreground">
            {data.lastUpdated}
          </span>
        </div>
        
        <div className="space-y-2">
          <div className="flex justify-between items-center">
            <span className="text-sm text-muted-foreground flex items-center gap-1">
              <Heart className="h-4 w-4 text-red-500" /> Heart Rate
            </span>
            <span className="font-medium">{data.heartRate}</span>
          </div>
          
          <div className="flex justify-between items-center">
            <span className="text-sm text-muted-foreground flex items-center gap-1">
              <TrendingUp className="h-4 w-4 text-blue-500" /> Blood Pressure
            </span>
            <span className="font-medium">{data.bloodPressure}</span>
          </div>
          
          <div className="flex justify-between items-center">
            <span className="text-sm text-muted-foreground flex items-center gap-1">
              <Activity className="h-4 w-4 text-purple-500" /> Health Score
            </span>
            <span className={cn("font-medium", getHealthScoreColor(data.healthScore))}>
              {data.healthScore}
            </span>
          </div>
          
          <div className="flex justify-between items-center">
            <span className="text-sm text-muted-foreground">Temperature</span>
            <span className="font-medium">{data.temperature}</span>
          </div>
          
          <div className="flex justify-between items-center">
            <span className="text-sm text-muted-foreground">Weight</span>
            <span className="font-medium">{data.weight}</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
